import { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Camera, RefreshCw, X, Upload } from "lucide-react";

export function PhotoCapture({ value, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const uploadRef = useRef(null);
  const [active, setActive] = useState(false);
  const [error, setError] = useState("");

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    setError("");
    const reader = new FileReader();
    reader.onload = () => onCapture(reader.result);
    reader.readAsDataURL(file);
    if (uploadRef.current) uploadRef.current.value = "";
  };

  const start = async () => {
    setError("");
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError("Camera not supported on this device/browser, or the page is not served over HTTPS.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      setActive(true); // video element mounts; effect below attaches the stream
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setError("Camera permission was blocked. Allow camera access in your browser and try again.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setError("No camera was found on this device.");
      } else if (name === "NotReadableError") {
        setError("The camera is already in use by another application.");
      } else {
        setError("Unable to access camera. Please allow camera permissions and try again.");
      }
    }
  };

  const stop = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setActive(false);
  };

  // Attach the stream once the <video> element is actually mounted.
  useEffect(() => {
    if (active && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      const p = videoRef.current.play();
      if (p && p.catch) p.catch(() => {});
    }
  }, [active]);

  useEffect(() => () => stop(), []);

  const snap = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    const size = Math.min(video.videoWidth, video.videoHeight);
    canvas.width = 480;
    canvas.height = 480;
    const ctx = canvas.getContext("2d");
    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;
    ctx.drawImage(video, sx, sy, size, size, 0, 0, 480, 480);
    onCapture(canvas.toDataURL("image/jpeg", 0.85));
    stop();
  };

  return (
    <div className="space-y-3">
      <div className="relative aspect-[4/5] w-full overflow-hidden rounded-md border border-border bg-neutral-900">
        {value && !active && (
          <img src={value} alt="Contractor" className="h-full w-full object-cover" data-testid="captured-photo" />
        )}
        {active && <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover" />}
        {!value && !active && (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-neutral-400">
            <Camera className="h-10 w-10" />
            <span className="text-sm">No photo captured</span>
          </div>
        )}
      </div>
      {error && <p className="text-sm text-destructive" data-testid="camera-error">{error}</p>}
      <div className="flex gap-2">
        {!active && (
          <Button type="button" variant="outline" className="flex-1" onClick={start} data-testid="start-camera-btn">
            <Camera className="mr-2 h-4 w-4" />
            {value ? "Retake photo" : "Open camera"}
          </Button>
        )}
        {!active && (
          <>
            <input ref={uploadRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="upload-photo-input" />
            <Button type="button" variant="outline" className="flex-1" onClick={() => uploadRef.current?.click()} data-testid="upload-photo-btn">
              <Upload className="mr-2 h-4 w-4" /> Upload photo
            </Button>
          </>
        )}
        {active && (
          <>
            <Button type="button" className="flex-1 bg-brand hover:bg-brand-dark" onClick={snap} data-testid="capture-photo-btn">
              <Camera className="mr-2 h-4 w-4" /> Capture
            </Button>
            <Button type="button" variant="outline" onClick={stop} data-testid="cancel-camera-btn">
              <X className="h-4 w-4" />
            </Button>
          </>
        )}
        {value && !active && (
          <Button type="button" variant="ghost" onClick={() => onCapture(null)} data-testid="clear-photo-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
